import React from 'react';
import { Box, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCommentLines } from '@fortawesome/pro-light-svg-icons';
// The solid face of the same icon: this app carries free-solid, not pro-solid.
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/free-solid-svg-icons';
import MssTooltip from '../../common/MssTooltip';
import { LIKERT_STEPS } from '../../common/LikertFaceScale';
import { formatDate, formatTime } from '../../common/helpers';

/**
 * The phone's date: an abbreviated month, and the year only when it is not this
 * one. Written out in full, a September date is wider than the column the four
 * of them can spare between them.
 * @param {String|Date} value The moment the feedback was sent
 * @returns {String} The date, short enough to read in a narrow column
 */
const shortDate = (value) => {
  const isThisYear = new Date(value).getFullYear() === new Date().getFullYear();
  return formatDate(value, isThisYear ? 'MMM D' : 'MMM D, YYYY');
};

/** Whether a submission says anything beyond its rating. */
export const hasComments = (feedback) =>
  !!(feedback.bugDetail || feedback.ideaDetail || feedback.changeDetail || feedback.otherDetail);

/**
 * The face someone picked, drawn as it was on the form — the same icon, in the
 * same colour from the scale's ramp, so a rating reads the same on both sides.
 */
export const RatingFace = ({ rating }) => {
  const step = LIKERT_STEPS[rating];
  if (!step) return <Box className="feedbackRating empty">—</Box>;

  return (
    <MssTooltip id={`adminFeedback__tooltip_rating_${rating}`} title={step.label}>
      <Box className={`feedbackRating ${step.key}`} component="span">
        <FontAwesomeIcon icon={step.icon} fixedWidth />
      </Box>
    </MssTooltip>
  );
};

/**
 * Builds the column definitions for the feedback table.
 *
 * A phone is given four of the six — whether it has been dealt with, how they
 * felt, who they are and when — and the four share the width rather than
 * keeping the measurements a wide screen can afford, so the table reads at a
 * glance instead of scrolling sideways. What is dropped here is in the
 * submission underneath, which is one tap away.
 * @param {Object} params Column configuration
 * @param {String} params.id ID prefix for generated element IDs
 * @param {Function} params.onToggleReviewed Called with the row whose state is turning over
 * @param {Boolean} params.isNarrow Whether the table is on a screen too narrow for all six
 * @returns {Array<Object>} The DataGrid column definitions
 */
export const getColumns = ({ id, onToggleReviewed, isNarrow }) => [
  {
    field: 'isReviewed',
    headerName: 'Reviewed',
    width: isNarrow ? 78 : 100,
    sortable: true,
    renderCell: ({ row }) => (
      <MssTooltip
        id={`${id}__tooltip_reviewed_${row.id}`}
        title={row.isReviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
      >
        <IconButton
          id={`${id}__button_reviewed_${row.id}`}
          className={`feedbackReviewToggle ${row.isReviewed ? 'reviewed' : ''}`}
          size="small"
          aria-label={row.isReviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
          aria-pressed={!!row.isReviewed}
          // The row opens the submission; this button only turns the flag over.
          onClick={(event) => {
            event.stopPropagation();
            onToggleReviewed(row);
          }}
        >
          <FontAwesomeIcon icon={row.isReviewed ? faCircleCheckSolid : faCircleCheck} />
        </IconButton>
      </MssTooltip>
    ),
  },
  ...(isNarrow
    ? []
    : [
        {
          field: 'comments',
          headerName: 'Comments',
          width: 110,
          sortable: false,
          valueGetter: (value, row) => hasComments(row),
          renderCell: ({ row }) =>
            hasComments(row) ? (
              <MssTooltip
                id={`${id}__tooltip_comments_${row.id}`}
                title="This one has something written"
              >
                <FontAwesomeIcon icon={faCommentLines} className="feedbackCommentIcon" />
              </MssTooltip>
            ) : null,
        },
      ]),
  {
    field: 'rating',
    headerName: 'Rating',
    width: isNarrow ? 58 : 90,
    // renderCell is handed the cell's params, not the component's own props.
    renderCell: ({ row }) => <RatingFace rating={row.rating} />,
  },
  {
    field: 'userId',
    headerName: 'User',
    ...(isNarrow ? { flex: 1, minWidth: 78 } : { width: 130 }),
  },
  ...(isNarrow ? [] : [{ field: 'courseId', headerName: 'Course', width: 120 }]),
  {
    field: 'createdAt',
    // "Submitted" and its sort arrow are wider than the date they sit over.
    headerName: isNarrow ? 'Date' : 'Submitted',
    ...(isNarrow ? { flex: 1, minWidth: 70 } : { width: 200 }),
    /*
     * The date alone on a phone: the time of day costs more width than the
     * four columns have between them, and the submission itself gives the
     * whole timestamp to whoever opens the row.
     */
    valueFormatter: (value) => {
      if (!value) return '';
      return isNarrow ? shortDate(value) : `${formatDate(value)}, ${formatTime(value)}`;
    },
  },
];
